import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Plus, Search, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function VehicleList() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const response = await api.get('/vehicles');
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to load vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vehicles?search=${searchTerm}`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post('/vehicles/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Vehicles uploaded successfully');
      loadVehicles();
    } catch (error) {
      console.error('Bulk upload failed:', error);
      toast.error('Failed to upload vehicles');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'car_number,manufacturer,model,leased_from\nKA01AB1234,Toyota,Innova,ABC Leasing\nKA02CD5678,Maruti,Ertiga,XYZ Motors';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vehicle_template.csv';
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shizah-blue"></div>
      </div>
    );
  }

  return (
    <div data-testid="vehicles-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Vehicles</h1>
          <p className="text-gray-600">Manage your vehicle fleet</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={downloadTemplate}
            data-testid="download-template-button"
            variant="outline"
          >
            Download CSV Template
          </Button>
          <label htmlFor="bulk-upload">
            <Button
              as="span"
              data-testid="bulk-upload-button"
              disabled={uploading}
              className="bg-shizah-green hover:bg-green-600 text-white cursor-pointer"
            >
              <Upload size={20} className="mr-2" />
              {uploading ? 'Uploading...' : 'Bulk Upload'}
            </Button>
          </label>
          <input
            id="bulk-upload"
            type="file"
            accept=".csv"
            onChange={handleBulkUpload}
            className="hidden"
            data-testid="bulk-upload-input"
          />
          <Link to="/vehicles/new">
            <Button data-testid="add-vehicle-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
              <Plus size={20} className="mr-2" />
              Add Vehicle
            </Button>
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex gap-3">
          <Input
            data-testid="vehicle-search-input"
            placeholder="Search by car number, manufacturer, or model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1"
          />
          <Button data-testid="search-button" onClick={handleSearch} className="bg-shizah-blue hover:bg-blue-700 text-white">
            <Search size={20} className="mr-2" />
            Search
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="vehicles-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Vehicle ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Car Number
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Manufacturer
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Leased From
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {vehicles.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No vehicles found. Add vehicles or upload via CSV.
                  </td>
                </tr>
              ) : (
                vehicles.map((vehicle) => (
                  <tr key={vehicle.vehicle_id} className="hover:bg-gray-50" data-testid={`vehicle-row-${vehicle.vehicle_id}`}>
                    <td className="px-6 py-4 text-sm font-semibold text-shizah-blue">{vehicle.vehicle_id}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-semibold">{vehicle.car_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{vehicle.manufacturer}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{vehicle.model}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{vehicle.leased_from}</td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          vehicle.status === 'available'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}
                      >
                        {vehicle.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}